import React, { useEffect, useRef, useState } from "react";

/**
 * VedAI FACE SYSTEM (NO EMOJI VERSION)
 * - Lip sync animation
 * - Mood expressions
 * - Eye blinking
 * - Talking animation
 */

export default function VedAIFace({ isTalking, mood = "neutral" }) {
  const [blink, setBlink] = useState(false);
  const [mouthOpen, setMouthOpen] = useState(0);
  const [tilt, setTilt] = useState(0);

  const animationRef = useRef(null);
  const blinkRef = useRef(null);

  // ==========================
  // BLINK SYSTEM
  // ==========================
  useEffect(() => {
    const blinkLoop = () => {
      const timeout = Math.random() * 4000 + 2000;

      blinkRef.current = setTimeout(() => {
        setBlink(true);

        setTimeout(() => {
          setBlink(false);
        }, 150);

        blinkLoop();
      }, timeout);
    };

    blinkLoop();

    return () => clearTimeout(blinkRef.current);
  }, []);

  // ==========================
  // TALKING / LIP SYNC SYSTEM
  // ==========================
  useEffect(() => {
    if (!isTalking) {
      setMouthOpen(0);
      cancelAnimationFrame(animationRef.current);
      return;
    }

    let frame = 0;

    const animate = () => {
      frame += 1;

      // fake audio-driven motion (natural variation)
      const wave =
        Math.abs(Math.sin(frame * 0.2)) * 0.8 +
        Math.random() * 0.2;

      setMouthOpen(wave);

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(animationRef.current);
  }, [isTalking]);

  // ==========================
  // MOOD SYSTEM
  // ==========================
  const moodMap = {
    happy: { eye: 1, mouth: 1, tilt: 2 },
    sad: { eye: 0.6, mouth: -1, tilt: -3 },
    angry: { eye: 0.8, mouth: -0.5, tilt: 4 },
    neutral: { eye: 1, mouth: 0, tilt: 0 },
  };

  const currentMood = moodMap[mood] || moodMap.neutral;

  // ==========================
  // FACE TRANSFORM VALUES
  // ==========================
  const headTilt = isTalking
    ? Math.sin(Date.now() / 400) * 2
    : currentMood.tilt;

  const eyeScale = blink ? 0.1 : currentMood.eye;

  const mouthScale = isTalking
    ? 0.5 + mouthOpen
    : 0.2 + currentMood.mouth * 0.1;

  // ==========================
  // RENDER FACE
  // ==========================
  return (
    <div style={styles.wrapper}>
      <div
        style={{
          ...styles.face,
          transform: `rotate(${headTilt}deg)`,
        }}
      >
        {/* HEAD */}
        <div style={styles.head}>
          {/* HAIR */}
          <div style={styles.hair} />

          {/* EYES */}
          <div style={styles.eyeRow}>
            <div
              style={{
                ...styles.eye,
                transform: `scaleY(${eyeScale})`,
              }}
            />
            <div
              style={{
                ...styles.eye,
                transform: `scaleY(${eyeScale})`,
              }}
            />
          </div>

          {/* EYEBROWS */}
          <div style={styles.brows}>
            <div style={styles.brow} />
            <div style={styles.brow} />
          </div>

          {/* NOSE */}
          <div style={styles.nose} />

          {/* MOUTH */}
          <div
            style={{
              ...styles.mouth,
              transform: `scaleY(${mouthScale})`,
            }}
          />

          {/* FACE GLOW */}
          <div
            style={{
              ...styles.glow,
              opacity: isTalking ? 0.5 : 0.2,
            }}
          />
        </div>
      </div>

      {/* STATUS TEXT (optional debug) */}
      <div style={styles.status}>
        {isTalking ? "🟢 Speaking..." : "⚪ Idle"}
      </div>
    </div>
  );
}

// ==========================
// STYLES (REALISTIC FACE)
// ==========================
const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginTop: "10px",
  },

  face: {
    width: "180px",
    height: "180px",
    position: "relative",
    transition: "0.2s ease",
  },

  head: {
    width: "100%",
    height: "100%",
    background: "linear-gradient(145deg, #f2c9a0, #e0a96d)",
    borderRadius: "50%",
    position: "relative",
    boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
    overflow: "hidden",
  },

  hair: {
    position: "absolute",
    top: "-10px",
    left: "10px",
    width: "160px",
    height: "80px",
    background: "#2b1b12",
    borderRadius: "50%",
  },

  eyeRow: {
    position: "absolute",
    top: "70px",
    width: "100%",
    display: "flex",
    justifyContent: "space-around",
  },

  eye: {
    width: "18px",
    height: "18px",
    background: "#000",
    borderRadius: "50%",
    transition: "0.15s",
  },

  brows: {
    position: "absolute",
    top: "55px",
    width: "100%",
    display: "flex",
    justifyContent: "space-around",
  },

  brow: {
    width: "25px",
    height: "4px",
    background: "#3b2a1a",
    borderRadius: "10px",
  },

  nose: {
    position: "absolute",
    top: "95px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "6px",
    height: "18px",
    background: "#d19b6a",
    borderRadius: "10px",
  },

  mouth: {
    position: "absolute",
    bottom: "40px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "45px",
    height: "10px",
    background: "#7a2e2e",
    borderRadius: "20px",
    transition: "0.1s",
  },

  glow: {
    position: "absolute",
    inset: 0,
    background:
      "radial-gradient(circle at center, rgba(0,255,200,0.2), transparent 60%)",
  },

  status: {
    marginTop: "10px",
    fontSize: "12px",
    color: "#9ca3af",
  },
};